import { UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import {
  TrainSearchItineraryQuery,
  TrainSearchItineraryQueryVariables,
} from 'generated/graphql';
import type { SearchQueryResult } from '../types';
import {
  arrayToMap,
  getPreferredCommercialCarrierId,
} from './transformItinerarySearchResponse';

type ItinerarySearchTrainsResponse = UseQueryResult<
  TrainSearchItineraryQuery,
  TrainSearchItineraryQueryVariables
>;

export const transformSearchItineraryTrainUnbundleResponse = (
  {
    data,
  }: {
    data: ItinerarySearchTrainsResponse['data'];
  },
  { selectedDepartureId }: { selectedDepartureId: string | null }
): SearchQueryResult['data'] => {
  const { searchTrains } = data || {};
  if (!searchTrains) return null;

  const {
    insuranceOffers,
    carbonFootprints,
    campaignConfigs,
    locations,
    carriers,
    priceTypeDisplayed,
    sections,
    segments,
    ...rest
  } = searchTrains;
  // Turn arrays into id maps so we can easily access them by id instead
  // of iterating through the array every time we need a value
  const itinerarySearchMap = {
    ...searchTrains,
    locations: arrayToMap(locations, 'location'),
    carriers: arrayToMap(carriers, 'carrier'),
    insuranceOffers: arrayToMap(insuranceOffers, 'insuranceOffer'),
    carbonFootprints: arrayToMap(carbonFootprints, 'carbonFootprint'),
    campaignConfigs: arrayToMap(campaignConfigs, 'campaignConfig'),
    sections: arrayToMap(sections, 'section'),
    segments: arrayToMap(segments, 'segment'),
  };

  const mapSegment = (segment) => {
    const segmentCommercialBrandId = getPreferredCommercialCarrierId(
      segment.carrierId,
      itinerarySearchMap.sections[segment.sections.length - 1].commercialBrandId
    );
    return {
      ...segment,
      __typename: 'Segment',
      sections: segment.sections.map((sectionId) => {
        const section = itinerarySearchMap.sections[sectionId];
        const {
          departureId,
          destinationId,
          carrierId,
          operatingCarrierId,
          commercialBrandId,
          insuranceOfferId,
          ...rest
        } = section;
        const sectionCommercialBrandId = getPreferredCommercialCarrierId(
          carrierId,
          commercialBrandId
        );
        return {
          ...rest,
          __typename: 'Section',
          insuranceOffer:
            insuranceOfferId != null
              ? itinerarySearchMap.insuranceOffers[insuranceOfferId]
              : null,
          departure: itinerarySearchMap.locations[departureId],
          destination: itinerarySearchMap.locations[destinationId],
          carrier: itinerarySearchMap.carriers[sectionCommercialBrandId],
          operatingCarrier:
            itinerarySearchMap.carriers[operatingCarrierId as string],
          commercialBrand:
            itinerarySearchMap.carriers[commercialBrandId as string],
        };
      }),
      carrier: itinerarySearchMap.carriers[segmentCommercialBrandId],
    };
  };

  return {
    search: {
      ...rest,
      __typename: 'SearchResponse',
      defaultFeeType: { id: priceTypeDisplayed as string },
      itineraries: selectedDepartureId
        ? // This part is for returns
          searchTrains.unbundledMapping[
            selectedDepartureId
          ].compatibleInboundSegments.map((itinerary, index) => {
            const bundledItinerary = searchTrains.itineraries.find(
              (item) => item.key === itinerary.itineraryKey
            );
            const segment =
              itinerarySearchMap.segments[itinerary.inboundSegmentId];

            return {
              __typename: 'Itinerary',
              key: itinerary.itineraryKey,
              id: bundledItinerary?.key,
              // isFareUpgradeAvailable and hotelXSellingEnabled have been deprecated
              // in the back-end
              isFareUpgradeAvailable: false,
              hotelXSellingEnabled: false,
              campaignConfig: null,
              carbonFootprint: null,
              ticketsLeft: segment.ticketsLeft,
              transportTypes: segment.transportTypes,
              meRating: bundledItinerary?.meRating,
              fees: bundledItinerary?.fees.map((fee) => ({
                price: fee.price,
                type: { id: fee.type },
              })),
              legs: [
                {
                  segmentKeys: [0],
                  segments: [mapSegment(segment)],
                },
              ],
            };
          })
        : // This part is for departures
          searchTrains?.unbundledMapping.map((itinerary, index) => {
            const segment =
              itinerarySearchMap.segments[itinerary.outboundSegmentId];

            return {
              __typename: 'Itinerary',
              key: index.toString(),
              id: index.toString(),
              // isFareUpgradeAvailable and hotelXSellingEnabled have been deprecated
              // in the back-end
              isFareUpgradeAvailable: false,
              hotelXSellingEnabled: false,
              campaignConfig: null,
              carbonFootprint: null,
              ticketsLeft: segment.ticketsLeft,
              fees: itinerary.cheapestFees.map((fee) => ({
                price: fee.price,
                type: { id: fee.type },
              })),
              transportTypes: segment.transportTypes,
              meRating: itinerary.bestMeRating,
              legs: [
                {
                  __typename: 'Leg',
                  segmentKeys: [0],
                  segments: [mapSegment(segment)],
                },
              ],
            };
          }),
    },
  };
};
